import CircularSkeleton from "#common/skeleton/CircularSkeleton"
import { DialogMaster } from "#core/components/dialog/Dialog"
import ErrorResponse from "#core/pages/ErrorResponse"
import { useApi } from "#hooks/useApi"
import { evaluationIdGet } from "#performance-evaluation/services/api"
import { Box, Divider, Grid, Paper, Rating, Stack, Typography } from "@mui/material"
import { Fragment, useMemo } from "react"

export default function MyEvaluationsDetails({ open, setOpen, evaluationId }) {
	const params = useMemo(
		() => ({
			id: evaluationId,
			params: { details: true },
		}),
		[evaluationId],
	)

	const { data, loading, error } = useApi(evaluationIdGet, {
		autoFetch: open,
		params,
	})
	const evaluation = useMemo(() => data?.evaluation ?? data?.evaluacion ?? null, [data])
	const evaluators = useMemo(() => evaluation?.evaluator ?? [], [evaluation])

	if (loading)
		return (
			<DialogMaster
				{...{
					open,
					setOpen,
					title: "Detalle de evaluación",
					maxWidth: "xl",
				}}
			>
				<CircularSkeleton />
			</DialogMaster>
		)
	if (error) return <ErrorResponse api={error} />
	return (
		<DialogMaster
			{...{
				open,
				setOpen,
				title: "Detalle de evaluación",
				maxWidth: "xl",
			}}
		>
			<Stack spacing={3}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<NoEvaluations evaluators={evaluators} />
					</Grid>
					{evaluators.toReversed().map((evaluator) =>
						evaluator?.score_competencies ? (
							<Grid item xs={12} md={6} key={`evaluator-${evaluator.id}`}>
								<Paper
									elevation={0}
									sx={{
										p: 2,
										borderRadius: 2,
										border: "1px solid",
										borderColor: "divider",
									}}
								>
									<Stack spacing={1.5}>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center"
										>
											<Typography variant="subtitle1" fontWeight={700}>
												{evaluator.evaluator_type_name || "Evaluador"}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Fecha de Evaluacion {evaluator?.evaluation_date}
											</Typography>
										</Stack>
										<Competences
											competencies={evaluator.competencies}
											score={evaluator.score_competencies}
										/>
										{evaluator.kpis?.length ? <Kpis kpis={evaluator.kpis} /> : null}
									</Stack>
								</Paper>
							</Grid>
						) : null,
					)}
					{evaluators.length === 0 ? (
						<Grid item xs={12}>
							<Paper
								elevation={0}
								sx={{
									p: 2,
									borderRadius: 2,
									border: "1px dashed",
									borderColor: "divider",
								}}
							>
								<Typography variant="body2" color="text.secondary">
									No hay evaluadores registrados para esta evaluación.
								</Typography>
							</Paper>
						</Grid>
					) : null}
				</Grid>
			</Stack>
		</DialogMaster>
	)
}

const NoEvaluations = ({ evaluators }) => {
	for (let evaluator of evaluators) if (evaluator?.score_competencies) return null
	return (
		<Box>
			<Typography variant="h5" color="text.secondary" textAlign={"center"}>
				No hay evaluaciones disponibles.
			</Typography>
		</Box>
	)
}

const Competences = ({ competencies, score }) => {
	return (
		<Box>
			<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
				Competencias {score}
			</Typography>
			<Divider sx={{ mb: 2 }} />
			{(competencies || []).map((comp) => (
				<Box key={`comp-${comp.id}`}>
					<Typography variant="body1" fontWeight={600}>
						{comp.competencia_name}
					</Typography>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={{ xs: 0.5, sm: 1.5 }}
						alignItems={{ xs: "flex-start", sm: "center" }}
						divider={<Divider orientation="vertical" flexItem />}
						sx={{ color: "text.secondary" }}
					>
						<Typography variant="body2">
							Puntaje Esperado: {comp.expectation ?? "-"}
						</Typography>
						<Typography variant="body2">
							Puntaje Obtenido: {Number(comp.score) || 0}
						</Typography>
					</Stack>
					<Box component="ul" sx={{ pl: 2, mt: 1, mb: 0, color: "text.secondary" }}>
						{(comp.questions || []).map((q) => (
							<Fragment key={`q-${q.id}`}>
								<li key={`q-${q.id}`}>{q.question_name}</li>
								<Stack direction="row" spacing={1} alignItems="center">
									<Rating
										value={Number(q.score) || 0}
										// precision={0.5}
										readOnly
										size="small"
									/>
									<Typography variant="caption" color="text.secondary">
										{Number(q.score) || 0}/5
									</Typography>
								</Stack>
							</Fragment>
						))}
					</Box>
					{comp.observations ? (
						<Typography variant="body2" sx={{ mt: 1 }}>
							Observaciones: {comp.observations}
						</Typography>
					) : null}
				</Box>
			))}
		</Box>
	)
}

const Kpis = ({ kpis }) => {
	return (
		<Box sx={{ mt: 2 }}>
			<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
				KPIs
			</Typography>
			<Divider sx={{ mb: 2 }} />
			<Stack spacing={1.5}>
				{(kpis || []).map((kpi) => (
					<Stack
						key={`kpi-${kpi.id}`}
						direction="column"
						justifyContent="space-between"
						alignItems="left"
					>
						<Typography variant="body1" fontWeight={600}>
							{kpi.kpi_name}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Esperado: {kpi.minimum_percentage ?? "-"}% - Real: {Number(kpi.score) || 0}%
						</Typography>
					</Stack>
				))}
			</Stack>
		</Box>
	)
}
